<main class="grow" role="content">
  <div class="kt-container-fixed bond-detail-container py-5 lg:py-7.5">
    <div class="kt-card">
      <div class="kt-card-header min-h-16 flex-wrap gap-3">
        <div class="flex min-w-0 flex-col gap-1">
          <div class="text-xs text-secondary-foreground">Bonds / Details</div>
          <h1 class="kt-card-title">Bond Details</h1>
        </div>

        <div class="inline-flex items-center gap-2.5 flex-wrap ms-auto">
          <span class="kt-badge kt-badge-outline">
            Last Modified By: {{ detail()?.modifiedBy || '' }}
            @if (detail()?.dateModified) {
              &nbsp;|&nbsp;{{ formatDateTimeOrBlank(detail()?.dateModified || '') }}
            }
          </span>

          @if (detail(); as headerDetail) {
            @if (!headerDetail.isNew) {
              <button
                type="button"
                class="kt-btn kt-btn-sm kt-btn-destructive"
                data-kt-modal-toggle="#bond_detail_delete_modal"
              >
                <i class="ki-filled ki-trash text-xs" aria-hidden="true"></i>
                <span>Delete Bond</span>
              </button>
            }
          }

          <button
            type="button"
            class="kt-btn kt-btn-sm kt-btn-light"
            data-kt-modal-toggle="#bond_detail_save_modal"
            (click)="prepareSaveIntent('save')"
            [disabled]="!hasUnsavedChanges()"
          >
            Save
          </button>
        </div>
      </div>

      <div class="kt-card-content bond-detail-content-shell p-5 lg:p-7.5">
        @if (isLoading()) {
          <section class="kt-card bond-detail-readonly-card">
            <div class="kt-card-content p-6">
              <div class="catalog-loading" role="status" aria-live="polite" aria-label="Loading bond detail">
                <span class="catalog-loading-spinner" aria-hidden="true"></span>
                <span class="catalog-loading-text">Loading...</span>
              </div>
            </div>
          </section>
        }

        @if (errorMessage()) {
          <section class="kt-alert kt-alert-destructive flex items-center justify-between gap-3">
            <span>{{ errorMessage() }}</span>
            <button type="button" class="kt-btn kt-btn-sm kt-btn-light" (click)="onRetry()">
              Retry
            </button>
          </section>
        }

        @if (!isLoading() && !errorMessage() && detail(); as detailData) {
          <section class="kt-card bond-detail-readonly-card">
            <div class="kt-card-content p-5">
              <div class="bond-detail-readonly-rows">
                <div class="bond-detail-readonly-row bond-detail-readonly-row-three">
                  <div class="bond-detail-readonly-item">
                    <span class="bond-detail-readonly-label">CCY - Notional</span>
                    <span class="bond-detail-readonly-value">{{ formatCurrencyWithCode(detailData.notional, detailData.ccy) }}</span>
                  </div>
                  <div class="bond-detail-readonly-item">
                    <span class="bond-detail-readonly-label">
                      @if (isFrnSecurity()) {
                        Index Name - Maturity Date
                      } @else {
                        Coupon - Maturity Date
                      }
                    </span>
                    <span class="bond-detail-readonly-value">{{ primaryRateMaturityValue() }}</span>
                  </div>
                  <div class="bond-detail-readonly-item">
                    <span class="bond-detail-readonly-label">Market Type</span>
                    <span class="bond-detail-readonly-value">{{ detailData.marketType || '-' }}</span>
                  </div>
                </div>

                <div class="bond-detail-readonly-row bond-detail-readonly-row-three">
                  <div class="bond-detail-readonly-item">
                    <span class="bond-detail-readonly-label">Trade Date</span>
                    <span class="bond-detail-readonly-value">{{ formatSettlementDate(detailData.tradeDate) }}</span>
                  </div>
                  <div class="bond-detail-readonly-item">
                    <span class="bond-detail-readonly-label">Settlement Date</span>
                    <span class="bond-detail-readonly-value">{{ formatSettlementDate(detailData.settlementDate) }}</span>
                  </div>
                  <div class="bond-detail-readonly-item">
                    <span class="bond-detail-readonly-label">Maturity Date</span>
                    <span class="bond-detail-readonly-value">{{ formatSettlementDate(detailData.maturityDate) }}</span>
                  </div>
                </div>

                <div class="bond-detail-readonly-row bond-detail-readonly-row-compact-six">
                  <div class="bond-detail-readonly-item bond-detail-readonly-item-compact">
                    <span class="bond-detail-readonly-label">Deal ID</span>
                    <span class="bond-detail-readonly-value">{{ detailData.dealId || '-' }}</span>
                  </div>
                  <div class="bond-detail-readonly-item bond-detail-readonly-item-compact">
                    <span class="bond-detail-readonly-label">Structure ID</span>
                    <span class="bond-detail-readonly-value">{{ detailData.structureId || '-' }}</span>
                  </div>
                  <div class="bond-detail-readonly-item bond-detail-readonly-item-compact">
                    <span class="bond-detail-readonly-label">Trade ID</span>
                    <span class="bond-detail-readonly-value">{{ detailData.tradeId || '-' }}</span>
                  </div>
                  <div class="bond-detail-readonly-item bond-detail-readonly-item-compact">
                    <span class="bond-detail-readonly-label">Security ID</span>
                    <span class="bond-detail-readonly-value">
                      @if (detailData.secId) {
                        <a
                          class="text-primary hover:underline"
                          [routerLink]="['/bond-definition', detailData.secId]"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {{ detailData.secId }}
                        </a>
                      } @else {
                        -
                      }
                    </span>
                  </div>
                  <div class="bond-detail-readonly-item bond-detail-readonly-item-compact">
                    <span class="bond-detail-readonly-label">CUSIP</span>
                    <span class="bond-detail-readonly-value">{{ detailData.cusip.trim() || '-' }}</span>
                  </div>
                  <div class="bond-detail-readonly-item bond-detail-readonly-item-compact">
                    <span class="bond-detail-readonly-label">ISIN</span>
                    <span class="bond-detail-readonly-value">{{ detailData.isin.trim() || '-' }}</span>
                  </div>
                </div>

                <div class="bond-detail-readonly-row bond-detail-readonly-row-compact-five">
                  <div class="bond-detail-readonly-item bond-detail-readonly-item-compact">
                    <span class="bond-detail-readonly-label">Location</span>
                    <span class="bond-detail-readonly-value">{{ detailData.location.trim() || '-' }}</span>
                  </div>
                  <div class="bond-detail-readonly-item bond-detail-readonly-item-compact">
                    <span class="bond-detail-readonly-label">Company</span>
                    <span class="bond-detail-readonly-value">{{ detailData.company.trim() || '-' }}</span>
                  </div>
                  <div class="bond-detail-readonly-item bond-detail-readonly-item-compact">
                    <span class="bond-detail-readonly-label">Desk</span>
                    <span class="bond-detail-readonly-value">{{ detailData.desk.trim() || '-' }}</span>
                  </div>
                  <div class="bond-detail-readonly-item bond-detail-readonly-item-compact">
                    <span class="bond-detail-readonly-label">Book</span>
                    <span class="bond-detail-readonly-value">{{ detailData.book.trim() || '-' }}</span>
                  </div>
                  <div class="bond-detail-readonly-item bond-detail-readonly-item-compact">
                    <span class="bond-detail-readonly-label">Folder</span>
                    <span class="bond-detail-readonly-value">{{ detailData.folder.trim() || '-' }}</span>
                  </div>
                </div>

                <div class="bond-detail-readonly-row bond-detail-readonly-row-single">
                  <div class="bond-detail-readonly-item bond-detail-readonly-item-compact">
                    <span class="bond-detail-readonly-label">TMS User (Data Entry)</span>
                    <span class="bond-detail-readonly-value">{{ detailData.doneUser || '-' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          @if (!isCallBond(detailData)) {
            @if (detailData.isNew) {
            <section class="kt-card bond-detail-gate-card">
              <div class="kt-card-content p-6">
                <h2 class="text-base font-semibold">Initialize this bond in Frida</h2>
                <p class="mt-2 text-sm text-secondary-foreground">
                  You are about to register bond details in Frida. Proceed to create the bond detail structure and continue with data entry.
                </p>

                @if (initializeError()) {
                  <div class="kt-alert kt-alert-destructive mt-4">
                    {{ initializeError() }}
                  </div>
                }

                <div class="mt-5">
                  <button
                    type="button"
                    class="kt-btn kt-btn-primary"
                    (click)="onProceedWithNewBond()"
                    [disabled]="isInitializing()"
                  >
                    @if (!isInitializing()) {
                      Proceed
                    } @else {
                      Processing...
                    }
                  </button>
                </div>
              </div>
            </section>
          } @else {
            <section class="kt-card bond-detail-tabs-card">
              <div class="kt-card-header min-h-14">
                <div
                  #tabsRoot
                  id="bond_detail_tabs"
                  class="kt-tabs kt-tabs-line bond-detail-tabs"
                  data-kt-tabs="true"
                  role="tablist"
                  aria-label="Bond detail tabs"
                >
                  @for (section of visibleSections(); track section.id; let index = $index) {
                    @let pendingReasons = sectionPendingReasons(section.id);
                    <button
                      type="button"
                      role="tab"
                      class="kt-tab-toggle"
                      [class.active]="index === 0"
                      [class.bond-tab-pending]="pendingReasons.length > 0"
                      [attr.data-kt-tab-toggle]="'#' + section.panelId"
                      [attr.aria-selected]="index === 0"
                      [attr.title]="pendingReasons.length > 0 ? 'Pending Items: ' + pendingReasons.join('; ') : null"
                    >
                      <i class="ki-filled" [class]="'ki-filled ' + section.iconClass" aria-hidden="true"></i>
                      {{ section.label }}
                      @if (pendingReasons.length > 0) {
                        <span class="bond-pending-dot" aria-hidden="true"></span>
                        <span class="sr-only">(has pending items)</span>
                      }
                    </button>
                  }
                </div>

              </div>

              <div class="kt-card-content p-5">
                @for (section of visibleSections(); track section.id; let index = $index) {
                  <section
                    class="bond-detail-tab-panel"
                    [class.hidden]="index !== 0"
                    [id]="section.panelId"
                    [attr.data-section]="section.id"
                  >
                    @if (section.id === 'bond-setup') {
                      <div class="bond-setup-grid">
                        @if (detailData.isSale && !isCdnSecurity()) {
                          <section class="bond-setup-section bond-form-field-full">
                            <div class="bond-setup-section-grid bond-main-details-grid">
                              <div class="bond-form-field bond-main-type-field">
                                <label class="kt-form-label" for="bondTypeId">
                                  Type
                                  @if (pendingItemFlags().type) {
                                    <span class="bond-pending-field-hint">Required</span>
                                  }
                                </label>
                                <select
                                  id="bondTypeId"
                                  class="kt-select"
                                  [class.bond-field-pending]="pendingItemFlags().type"
                                  [attr.aria-invalid]="pendingItemFlags().type"
                                  [value]="detailData.bondTypeId !== null ? detailData.bondTypeId.toString() : ''"
                                  (change)="updateBondType($any($event.target).value)"
                                >
                                  <option value="">-</option>
                                  @for (type of detailData.bondTypes; track trackBondType($index, type)) {
                                    <option
                                      [value]="type.id.toString()"
                                      [selected]="isBondTypeSelected(detailData.bondTypeId, type.id)"
                                    >
                                      {{ type.name }}
                                    </option>
                                  }
                                </select>
                              </div>

                              <div class="bond-related-fields-row bond-form-field-full">
                                <div class="bond-form-field">
                                  <label class="kt-form-label" for="bondPricingSupplement">
                                    Pricing Supplement - Series Number
                                  </label>
                                  <input
                                    id="bondPricingSupplement"
                                    class="kt-input"
                                    type="text"
                                    [value]="detailData.bondPricingSupplement"
                                    (input)="updateTextField('bondPricingSupplement', $any($event.target).value)"
                                    disabled=""
                                  />
                                </div>

                                <div class="bond-form-field">
                                  <label class="kt-form-label" for="tranche">
                                    Tranche
                                    @if (pendingItemFlags().tranche) {
                                      <span class="bond-pending-field-hint">Required</span>
                                    }
                                  </label>
                                  <input
                                    id="tranche"
                                    class="kt-input"
                                    [class.bond-field-pending]="pendingItemFlags().tranche"
                                    [attr.aria-invalid]="pendingItemFlags().tranche"
                                    type="text"
                                    [value]="detailData.tranche"
                                    (input)="updateTextField('tranche', $any($event.target).value)"
                                  />
                                </div>

                                <div class="bond-form-field">
                                  <label class="kt-form-label">Auto-calculated Value</label>
                                  @if (hasAutoTranche()) {
                                    <label class="kt-label flex items-center gap-2 bond-auto-toggle">
                                      <input
                                        type="checkbox"
                                        [checked]="isAutoTrancheApplied()"
                                        (change)="onAutoTrancheToggle($any($event.target).checked)"
                                      />
                                      <input class="kt-input" type="text" [value]="detailData.autoTranche || '-'" disabled="" />
                                    </label>
                                  } @else {
                                    <p class="bond-field-hint">No auto-calculation values available.</p>
                                  }
                                </div>
                              </div>
                            </div>
                          </section>
                        }

                        @if (!detailData.isSale) {
                          <section class="bond-setup-section bond-form-field-full">
                            <div class="bond-setup-section-header">
                              <h3 class="text-sm font-semibold">Repurchase Metrics</h3>
                            </div>

                            <div class="bond-setup-section-grid bond-setup-section-grid-wide bond-repurc-metrics-grid">
                              <div class="bond-form-field bond-form-field-numeric">
                                <label class="kt-form-label" for="bondGlRepurchase">
                                  Gain/Loss Repurchase in USD eq.= Funding Cost of Repurchase Debt - Funding Cost of Retired Debt
                                </label>
                                <input
                                  id="bondGlRepurchase"
                                  class="kt-input"
                                  type="text"
                                  inputmode="decimal"
                                  [value]="formatRepurcNumber(detailData.bondGlRepurchase)"
                                  (input)="onRepurcNumberInput($event, 'bondGlRepurchase')"
                                />
                              </div>

                              <div class="bond-form-field bond-form-field-numeric">
                                <label class="kt-form-label" for="bondGlRefinance">
                                  Gain/Loss Refinance in USD eq.= Funding Cost of Retired Debt - New Funding Cost at Similar Maturity
                                </label>
                                <input
                                  id="bondGlRefinance"
                                  class="kt-input"
                                  type="text"
                                  inputmode="decimal"
                                  [value]="formatRepurcNumber(detailData.bondGlRefinance)"
                                  (input)="onRepurcNumberInput($event, 'bondGlRefinance')"
                                />
                              </div>
                            </div>
                          </section>
                        }

                        @if (!isCdnSecurity()) {
                          <section class="bond-setup-section bond-form-field-full">
                            <div class="bond-setup-section-grid">
                              @if (detailData.isSale) {
                                <div class="bond-form-pair bond-form-pair-compact bond-form-field-full">
                                  <div class="bond-form-field bond-form-field-numeric">
                                    <label class="kt-form-label" for="sofrCost">
                                      ALL-IN USD SOFR Cost
                                      @if (pendingItemFlags().fundingCost) {
                                        <span class="bond-pending-field-hint">Required</span>
                                      }
                                    </label>
                                    <input
                                      id="sofrCost"
                                      class="kt-input"
                                      [class.bond-field-pending]="pendingItemFlags().fundingCost"
                                      [attr.aria-invalid]="pendingItemFlags().fundingCost"
                                      type="number"
                                      step="any"
                                      [value]="detailData.sofrCost ?? ''"
                                      (input)="updateNumberField('sofrCost', $any($event.target).value)"
                                    />
                                  </div>

                                  <div class="bond-form-field">
                                    <label class="kt-form-label">Auto-calculated Value</label>
                                    @if (hasAutoSofrCost()) {
                                      <label class="kt-label flex items-center gap-2 bond-auto-toggle">
                                        <input
                                          type="checkbox"
                                          [checked]="isAutoSofrCostApplied()"
                                          (change)="onAutoSofrCostToggle($any($event.target).checked)"
                                        />
                                        <input class="kt-input" type="text" [value]="formatCurrencyLike(detailData.autoSofrCost)" disabled="" />
                                      </label>
                                    } @else {
                                      <p class="bond-field-hint">No auto-calculation values available.</p>
                                    }
                                  </div>
                                </div>
                              } @else {
                                <div class="bond-form-field bond-form-field-numeric">
                                  <label class="kt-form-label" for="us3MlCost">Buyback level to dealer - Spread for Repurchase</label>
                                  <input
                                    id="us3MlCost"
                                    class="kt-input"
                                    type="text"
                                    inputmode="decimal"
                                    [value]="formatRepurcNumber(detailData.us3MlCost)"
                                    (input)="onRepurcNumberInput($event, 'us3MlCost')"
                                  />
                                </div>

                                <div class="bond-form-pair bond-form-field-full">
                                  <div class="bond-form-field bond-form-field-numeric">
                                    <label class="kt-form-label" for="repurcOriginalCost">Repurchase Original Cost</label>
                                    <input
                                      id="repurcOriginalCost"
                                      class="kt-input"
                                      type="text"
                                      inputmode="decimal"
                                      [value]="formatRepurcNumber(detailData.repurcOriginalCost)"
                                      (input)="onRepurcNumberInput($event, 'repurcOriginalCost')"
                                    />
                                  </div>

                                  <div class="bond-form-field">
                                    <label class="kt-form-label">Auto-calculated Value</label>
                                    @if (hasAutoUs3MlCost()) {
                                      <label class="kt-label flex items-center gap-2 bond-auto-toggle">
                                        <input
                                          type="checkbox"
                                          [checked]="isAutoUs3MlCostApplied()"
                                          (change)="onAutoUs3MlCostToggle($any($event.target).checked)"
                                        />
                                        <input class="kt-input" type="text" [value]="formatCurrencyLike(detailData.autoUs3MlCost)" disabled="" />
                                      </label>
                                    } @else {
                                      <p class="bond-field-hint">No auto-calculation values available.</p>
                                    }
                                  </div>
                                </div>

                                <div class="bond-form-field bond-form-field-numeric">
                                  <label class="kt-form-label" for="repurcNewFundingCost">
                                    Repurchase New Funding Cost at similar maturity in bps
                                  </label>
                                  <input
                                    id="repurcNewFundingCost"
                                    class="kt-input"
                                    type="text"
                                    inputmode="decimal"
                                    [value]="formatRepurcNumber(detailData.repurcNewFundingCost)"
                                    (input)="onRepurcNumberInput($event, 'repurcNewFundingCost')"
                                  />
                                </div>
                              }
                            </div>
                          </section>

                          @if (detailData.isSale) {
                            <section class="bond-setup-section bond-form-field-full">
                              <div class="bond-setup-section-header bond-benchmark-header">
                                <h3 class="text-sm font-semibold bond-benchmark-title">Spread over other Benchmark</h3>
                                <button
                                  type="button"
                                  class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                                  data-kt-modal-toggle="#bond_detail_add_bmk_modal"
                                  (click)="prepareAddBmkModal()"
                                  aria-label="Add reoffer"
                                >
                                  <i class="ki-filled ki-plus" aria-hidden="true"></i>
                                </button>
                              </div>

                              @if (detailData.bondTradeBmks.length === 0) {
                                <p class="bond-field-hint text-center">No spread rows available.</p>
                              } @else {
                                <div class="kt-table-wrapper kt-scrollable">
                                  <table class="kt-table">
                                    <colgroup>
                                      <col style="width: 40%;" />
                                      <col style="width: 40%;" />
                                      <col style="width: 20%;" />
                                    </colgroup>
                                    <thead>
                                      <tr>
                                        <th>Reoffer names</th>
                                        <th>Spread over Benchmark in bps</th>
                                        <th class="text-center">Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      @for (bmk of detailData.bondTradeBmks; track trackBondTradeBmk($index, bmk)) {
                                        <tr>
                                          <td>
                                            {{ (bmk.benchmark || bmk.tenor) ? ((bmk.benchmark || '-') + (bmk.tenor ? ' - ' + bmk.tenor : '')) : '-' }}
                                          </td>
                                          <td>{{ bmk.value || '-' }}</td>
                                          <td class="text-center">
                                            <span class="inline-flex items-center gap-2">
                                              <button
                                                type="button"
                                                class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                                                data-kt-modal-toggle="#bond_detail_add_bmk_modal"
                                                (click)="startEditBmkRow(bmk)"
                                                [disabled]="isMutatingBmk()"
                                                aria-label="Edit reoffer row"
                                              >
                                                <i class="ki-filled ki-pencil" aria-hidden="true"></i>
                                              </button>
                                              <button
                                                type="button"
                                                class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                                                (click)="requestDeleteBmkRow(bmk)"
                                                [disabled]="isMutatingBmk()"
                                                aria-label="Delete reoffer row"
                                              >
                                                <i class="ki-filled ki-trash" aria-hidden="true"></i>
                                              </button>
                                            </span>
                                          </td>
                                        </tr>
                                      }
                                    </tbody>
                                  </table>
                                </div>
                              }
                            </section>
                          }
                        }
                      </div>
                    } @else if (section.id === 'legacy') {
                      <div class="bond-tab-stack">
                        <section class="bond-tab-card">
                          <div class="bond-form-pair bond-form-pair-compact">
                            <div class="bond-form-field bond-form-field-numeric">
                              <label class="kt-form-label" for="us3MlCost">ALL-IN USD 3mL Cost</label>
                              <input
                                id="us3MlCost"
                                class="kt-input"
                                type="number"
                                step="any"
                                [value]="detailData.us3MlCost ?? ''"
                                (input)="updateNumberField('us3MlCost', $any($event.target).value)"
                              />
                            </div>

                            <div class="bond-form-field">
                              <label class="kt-form-label">Auto-calculated Value</label>
                              @if (hasAutoUs3MlCost()) {
                                <label class="kt-label flex items-center gap-2 bond-auto-toggle">
                                  <input
                                    type="checkbox"
                                    [checked]="isAutoUs3MlCostApplied()"
                                    (change)="onAutoUs3MlCostToggle($any($event.target).checked)"
                                  />
                                  <input class="kt-input" type="text" [value]="formatCurrencyLike(detailData.autoUs3MlCost)" disabled="" />
                                </label>
                              } @else {
                                <p class="bond-field-hint">No auto-calculation values available.</p>
                              }
                            </div>
                          </div>
                        </section>
                      </div>
                    } @else if (section.id === 'related-instruments') {
                      <div class="bond-tab-stack">
                        <div class="flex items-center justify-between gap-2">
                          <h2 class="text-base font-semibold">Related Instruments</h2>
                        </div>

                        <ng-template #relatedInstrumentsHeader>
                          <tr>
                            <th scope="col" data-kt-datatable-column="dealId"><span class="kt-table-col"><span class="kt-table-col-label">Deal ID</span><span class="kt-table-col-sort"></span></span></th>
                            <th scope="col" data-kt-datatable-column="structureId"><span class="kt-table-col"><span class="kt-table-col-label">Structure ID</span><span class="kt-table-col-sort"></span></span></th>
                            <th scope="col" data-kt-datatable-column="tradeId"><span class="kt-table-col"><span class="kt-table-col-label">Trade ID</span><span class="kt-table-col-sort"></span></span></th>
                            <th scope="col" data-kt-datatable-column="securityId"><span class="kt-table-col"><span class="kt-table-col-label">Security ID</span><span class="kt-table-col-sort"></span></span></th>
                            <th scope="col" data-kt-datatable-column="marketType"><span class="kt-table-col"><span class="kt-table-col-label">Market Type</span><span class="kt-table-col-sort"></span></span></th>
                            <th scope="col" data-kt-datatable-column="payCcyNotional"><span class="kt-table-col"><span class="kt-table-col-label">Pay Notional</span><span class="kt-table-col-sort"></span></span></th>
                            <th scope="col" data-kt-datatable-column="receiveCcyNotional"><span class="kt-table-col"><span class="kt-table-col-label">Receive Notional</span><span class="kt-table-col-sort"></span></span></th>
                            <th scope="col" data-kt-datatable-column="ccyNotional" class="related-instruments-ccy-notional-col"><span class="kt-table-col"><span class="kt-table-col-label">Notional</span><span class="kt-table-col-sort"></span></span></th>
                            <th scope="col" data-kt-datatable-column="coupon"><span class="kt-table-col"><span class="kt-table-col-label">Coupon</span><span class="kt-table-col-sort"></span></span></th>
                            <th scope="col" data-kt-datatable-column="couponPay"><span class="kt-table-col"><span class="kt-table-col-label">Coupon Pay</span><span class="kt-table-col-sort"></span></span></th>
                            <th scope="col" data-kt-datatable-column="couponReceive"><span class="kt-table-col"><span class="kt-table-col-label">Coupon Receive</span><span class="kt-table-col-sort"></span></span></th>
                            <th scope="col" data-kt-datatable-column="tradeDate"><span class="kt-table-col"><span class="kt-table-col-label">Trade Date</span><span class="kt-table-col-sort"></span></span></th>
                            <th scope="col" data-kt-datatable-column="settlementDate"><span class="kt-table-col"><span class="kt-table-col-label">Settlement Date</span><span class="kt-table-col-sort"></span></span></th>
                            <th scope="col" data-kt-datatable-column="maturityDate"><span class="kt-table-col"><span class="kt-table-col-label">Maturity Date</span><span class="kt-table-col-sort"></span></span></th>
                          </tr>
                        </ng-template>

                        @if (relatedInstrumentsError()) {
                          <div class="kt-alert kt-alert-destructive" role="alert">
                            <div class="kt-alert-text">{{ relatedInstrumentsError() }}</div>
                          </div>
                        }

                        @if (isLoadingRelatedInstruments()) {
                          <div class="catalog-loading" role="status" aria-live="polite" aria-label="Loading related instruments">
                            <span class="catalog-loading-spinner" aria-hidden="true"></span>
                            <span class="catalog-loading-text">Loading related instruments...</span>
                          </div>
                        } @else if (detailData.relatedInstruments.length === 0) {
                          <div class="kt-card-table">
                            <div class="kt-table-wrapper kt-scrollable">
                              <table class="kt-table">
                                <thead>
                                  <ng-container [ngTemplateOutlet]="relatedInstrumentsHeader"></ng-container>
                                </thead>
                                <tbody>
                                  <tr>
                                    <td colspan="14" class="text-center text-secondary-foreground py-6">
                                      No related instruments were found.
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        } @else {
                          <div
                            id="kt_related_instruments_datatable"
                            class="kt-card-table"
                            data-kt-datatable="true"
                            data-kt-datatable-page-size="10"
                            data-kt-datatable-state-save="true"
                            data-kt-datatable-state-namespace="bond-detail-related-instruments-datatable"
                          >
                            <div class="kt-table-wrapper kt-scrollable">
                            <table class="kt-table" data-kt-datatable-table="true">
                              <thead>
                                <ng-container [ngTemplateOutlet]="relatedInstrumentsHeader"></ng-container>
                              </thead>
                              <tbody>
                                  @for (instrument of detailData.relatedInstruments; track trackRelatedInstrument($index, instrument)) {
                                    <tr>
                                      <td>{{ instrument.dealId || '-' }}</td>
                                      <td>{{ instrument.structureId || '-' }}</td>
                                      <td>
                                        @if (instrument.tradeId) {
                                          <a
                                            class="text-primary hover:underline"
                                            [routerLink]="[relatedTradeDetailRoute(instrument.instrumentType), instrument.tradeId]"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                          >
                                            {{ instrument.tradeId }}
                                          </a>
                                        } @else {
                                          -
                                        }
                                      </td>
                                      <td>
                                        @if (instrument.securityId) {
                                          <a
                                            class="text-primary hover:underline"
                                            [routerLink]="['/bond-definition', instrument.securityId]"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                          >
                                            {{ instrument.securityId }}
                                          </a>
                                        } @else {
                                          -
                                        }
                                      </td>
                                      <td>{{ instrument.marketType || '-' }}</td>
                                      <td>{{ instrument.payCcyNotional || '-' }}</td>
                                      <td>{{ instrument.receiveCcyNotional || '-' }}</td>
                                      <td class="related-instruments-ccy-notional-col">{{ instrument.ccyNotional || '-' }}</td>
                                      <td>{{ formatRelatedCoupon(instrument.coupon) }}</td>
                                      <td>{{ formatRelatedCoupon(instrument.couponPay) }}</td>
                                      <td>{{ formatRelatedCoupon(instrument.couponReceive) }}</td>
                                      <td>{{ formatSettlementDate(instrument.tradeDate) }}</td>
                                      <td>{{ formatSettlementDate(instrument.settlementDate) }}</td>
                                      <td>{{ formatSettlementDate(instrument.maturityDate) }}</td>
                                    </tr>
                                  }
                              </tbody>
                            </table>
                          </div>

                            <div class="flex items-center justify-between gap-4 flex-wrap px-5 py-4">
                              <div data-kt-datatable-info="true" class="kt-datatable-info text-sm text-secondary-foreground"></div>
                              <div class="kt-datatable-length flex items-center gap-3">
                                <select data-kt-datatable-size="true" class="kt-select kt-select-sm w-20"></select>
                                <div data-kt-datatable-pagination="true" class="kt-datatable-pagination flex items-center gap-2"></div>
                              </div>
                            </div>
                          </div>
                        }
                      </div>
                    } @else if (section.id === 'pricing-supplements') {
                      <app-bond-pricing-supplement
                        [tradeId]="detailData.tradeId || tradeId"
                        [executedDocsSignatureRemoved]="detailData.executedDocsSignatureRemoved"
                        [isPrivate]="detailData.isPrivatePricingSupplement"
                        [flagsDisabled]="isSaving()"
                        [reloadKey]="pricingSupplementReloadKey()"
                        (flagChange)="onDocumentationFlagChange($event.flag, $event.checked)"
                      />
                    } @else if (section.id === 'bond-fees') {
                      <div class="bond-tab-stack">
                        <section class="bond-tab-card">
                          <div class="bond-tab-card-header bond-fees-card-header">
                            <h3 class="bond-fees-section-title">Fees stated in the Pricing Supplement</h3>
                          </div>

                          @if (hasFeesInPricingSupplement()) {
                            <div class="bond-fees-grid">
                              <div class="bond-form-field">
                                <label class="kt-form-label">Fee Currency</label>
                                <input class="kt-input kt-input-sm bond-fees-input" type="text" [value]="detailData.issFeeCurrency" disabled="" />
                              </div>
                              <div class="bond-form-field">
                                <label class="kt-form-label">Fee Amount</label>
                                <input class="kt-input kt-input-sm bond-fees-input bond-fees-input-numeric text-end" type="text" [value]="formatAbsoluteCurrencyLike(detailData.issFee)" disabled="" />
                              </div>
                            </div>
                          } @else {
                            <p class="bond-empty-state bond-empty-state-warn">No fees stated in the Pricing Supplement.</p>
                          }
                        </section>

                        <section class="bond-tab-card">
                          <div class="bond-tab-card-header bond-fees-card-header">
                            <h3 class="bond-fees-section-title">Additional Fees</h3>
                          </div>

                          <div class="bond-fees-grid bond-fees-grid-other">
                            <div class="bond-form-field">
                              <label class="kt-form-label">CCY</label>
                              <input class="kt-input kt-input-sm bond-fees-input bond-fees-input-short text-center" type="text" [value]="detailData.ccy" disabled="" />
                            </div>

                            <div class="bond-form-field">
                              <label class="kt-form-label" for="bondTheoreticalFee">Other</label>
                              <input
                                id="bondTheoreticalFee"
                                class="kt-input kt-input-sm bond-fees-input bond-fees-input-numeric text-end"
                                type="number"
                                step="any"
                                [value]="detailData.bondTheoreticalFee ?? ''"
                                (input)="updateNumberField('bondTheoreticalFee', $any($event.target).value)"
                              />
                            </div>

                            <div class="bond-form-field">
                              <label class="kt-form-label" for="bpsPerYear">BPS per year</label>
                              <input
                                id="bpsPerYear"
                                class="kt-input kt-input-sm bond-fees-input bond-fees-input-numeric text-end"
                                type="number"
                                min="0"
                                step="any"
                                [value]="bpsPerYear()"
                                (change)="onBpsPerYearChange($any($event.target).value)"
                              />
                            </div>
                          </div>
                        </section>
                      </div>
                    } @else if (section.id === 'investors-allocations') {
                      <div class="bond-tab-stack">
                        <div class="flex items-center justify-between gap-2">
                          <h2 class="text-base font-semibold">Investors & Allocations</h2>
                          <button
                            type="button"
                            class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                            data-kt-modal-toggle="#bond_detail_add_investors_modal"
                            (click)="prepareAddInvestorsModal()"
                            aria-label="Add investors"
                          >
                            <i class="ki-filled ki-plus" aria-hidden="true"></i>
                          </button>
                        </div>

                        @if (addInvestorsError()) {
                          <div class="kt-alert kt-alert-destructive" role="alert">
                            <div class="kt-alert-text">{{ addInvestorsError() }}</div>
                          </div>
                        }

                        @if (detailData.investors.length === 0) {
                          <p class="bond-empty-state">No investor allocations were found for this bond.</p>
                        } @else {
                          <div class="kt-table-wrapper kt-scrollable">
                            <table class="kt-table">
                              <thead>
                                <tr>
                                  <th>Investor</th>
                                  <th>Type</th>
                                  <th>Country</th>
                                  <th>Region</th>
                                  <th>Group</th>
                                  <th class="text-end">Allocation</th>
                                  <th class="text-center">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                @for (investor of detailData.investors; track trackInvestor($index, investor)) {
                                  <tr>
                                    <td>{{ investor.name || '-' }}</td>
                                    <td>{{ investor.investorTypeName || '-' }}</td>
                                    <td>{{ investor.country || '-' }}</td>
                                    <td>{{ investor.region || '-' }}</td>
                                    <td>{{ investor.investorGroupName || '-' }}</td>
                                    <td class="text-end">
                                      @if (isMainInvestorAllocationEditing(investor.id)) {
                                        <input
                                          class="kt-input kt-input-sm bond-investor-allocation-input"
                                          type="text"
                                          inputmode="decimal"
                                          [value]="editingMainInvestorAllocationValue()"
                                          (input)="updateMainInvestorAllocationValue($any($event.target).value)"
                                          (keydown)="onMainInvestorAllocationInputKeydown($event)"
                                          placeholder="Enter allocation"
                                        />
                                      } @else {
                                        {{ formatCurrencyLike(investor.allocation) }}
                                      }
                                    </td>
                                    <td class="text-center">
                                      @if (isMainInvestorAllocationEditing(investor.id)) {
                                        <span class="inline-flex items-center gap-2">
                                          <button
                                            type="button"
                                            class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                                            aria-label="Cancel allocation edit"
                                            [disabled]="isMutatingInvestor()"
                                            (click)="cancelMainInvestorAllocationEdit(investor.id)"
                                          >
                                            <i class="ki-filled ki-cross" aria-hidden="true"></i>
                                          </button>
                                          <button
                                            type="button"
                                            class="kt-btn kt-btn-sm kt-btn-icon"
                                            aria-label="Save investor allocation"
                                            [disabled]="isMutatingInvestor()"
                                            (click)="saveMainInvestorAllocation(investor)"
                                          >
                                            <i class="ki-filled ki-check" aria-hidden="true"></i>
                                          </button>
                                        </span>
                                      } @else {
                                        <span class="inline-flex items-center gap-2">
                                          <button
                                            type="button"
                                            class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                                            aria-label="Edit investor allocation"
                                            [disabled]="isMutatingInvestor()"
                                            (click)="startMainInvestorAllocationEdit(investor)"
                                          >
                                            <i class="ki-filled ki-pencil" aria-hidden="true"></i>
                                          </button>
                                          <button
                                            type="button"
                                            class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                                            aria-label="Delete investor"
                                            [disabled]="isMutatingInvestor()"
                                            (click)="requestDeleteMainInvestor(investor)"
                                          >
                                            <i class="ki-filled ki-trash" aria-hidden="true"></i>
                                          </button>
                                        </span>
                                      }
                                    </td>
                                  </tr>
                                }
                              </tbody>
                            </table>
                          </div>
                        }

                        <section class="bond-investor-summary" [class]="investorSummaryContainerClass()">
                          <div class="bond-investor-summary-row">
                            <span class="bond-investor-summary-label">Notional Issued:</span>
                            <span class="bond-investor-summary-value">
                              {{ formatCurrencyLike(detailData.notional) }} {{ detailData.ccy }}
                            </span>
                          </div>
                          <div class="bond-investor-summary-row">
                            <span class="bond-investor-summary-label">Allocated to Investors:</span>
                            <span class="bond-investor-summary-value">
                              {{ formatCurrencyLike(totalInvestorAllocation()) }} {{ detailData.ccy }}
                            </span>
                          </div>
                          <div class="bond-investor-summary-row">
                            <span class="bond-investor-summary-label">Status:</span>
                            <span class="bond-investor-summary-value" [style.color]="investorSummaryColor()">
                              {{ investorAllocationStatusLabel() }}
                            </span>
                            @if (investorAllocationDelta() !== null && investorAllocationDelta() !== 0) {
                              <span class="bond-investor-summary-delta">
                                by {{ formatCurrencyLike(absoluteValue(investorAllocationDelta())) }} {{ detailData.ccy }}
                              </span>
                            }
                          </div>
                        </section>
                      </div>
                    } @else if (section.id === 'leads-co-leads') {
                      <div class="bond-tab-stack">
                        <div class="flex items-center justify-between gap-2">
                          <h2 class="text-base font-semibold">Leads / Co-Leads</h2>
                          <button
                            type="button"
                            class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                            data-kt-modal-toggle="#bond_detail_add_lead_modal"
                            (click)="prepareAddLeadModal()"
                            aria-label="Add Lead"
                          >
                            <i class="ki-filled ki-plus" aria-hidden="true"></i>
                          </button>
                        </div>

                        @if (detailData.tradeLead.length === 0 && pendingLeads().length === 0) {
                          <p class="bond-empty-state">No lead/co-lead rows were found for this bond.</p>
                        } @else {
                          @if (pendingLeads().length > 0) {
                            <div class="bond-pending-leads-bar" role="status">
                              <span class="bond-pending-leads-bar-text">
                                <i class="ki-filled ki-information-2" aria-hidden="true"></i>
                                {{ pendingLeads().length }} unsaved {{ pendingLeads().length === 1 ? 'lead' : 'leads' }}.
                                Select a Role for each row to save it.
                              </span>
                              <span class="inline-flex items-center gap-2">
                                <button
                                  type="button"
                                  class="kt-btn kt-btn-sm kt-btn-outline"
                                  [disabled]="isSavingPendingLeads()"
                                  (click)="discardPendingLeads()"
                                >
                                  Discard all
                                </button>
                                <button
                                  type="button"
                                  class="kt-btn kt-btn-sm kt-btn-primary"
                                  [disabled]="isSavingPendingLeads() || isMutatingLead() || pendingLeadsReadyCount() === 0"
                                  [title]="pendingLeadsReadyCount() === 0 ? 'Select a Role on at least one row' : ''"
                                  (click)="saveAllPendingLeads()"
                                >
                                  <i class="ki-filled ki-check" aria-hidden="true"></i>
                                  {{ isSavingPendingLeads() ? 'Saving...' : 'Save all (' + pendingLeadsReadyCount() + ')' }}
                                </button>
                              </span>
                            </div>
                          }
                          <div class="kt-table-wrapper kt-scrollable">
                            <table class="kt-table">
                              <thead>
                                <tr>
                                  <th>Bank Name</th>
                                  <th>Role</th>
                                  <th>Currency</th>
                                  <th class="text-center">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                @for (pending of pendingLeads(); track pending.customerId) {
                                  <tr class="bond-lead-pending-row" [class.bond-lead-pending-row-error]="pending.error">
                                    <td>
                                      <div class="flex items-center gap-2">
                                        <span>{{ pending.customerName }}</span>
                                        <span class="kt-badge kt-badge-sm kt-badge-warning kt-badge-outline">Unsaved</span>
                                      </div>
                                      @if (pending.error) {
                                        <div class="bond-lead-pending-error">{{ pending.error }}</div>
                                      }
                                    </td>
                                    <td>
                                      <select
                                        class="kt-select kt-select-sm bond-lead-inline-role"
                                        aria-label="Role"
                                        [class.bond-lead-role-missing]="!pending.role"
                                        [value]="pending.role"
                                        [disabled]="pending.saving || isLeadRoleLocked()"
                                        (change)="onPendingLeadRoleChange(pending.customerId, $any($event.target).value)"
                                      >
                                        @if (!isLeadRoleLocked()) {
                                          <option value="" [selected]="pending.role === ''">Select role</option>
                                        }
                                        @for (role of leadRoleOptions(); track role) {
                                          <option [value]="role" [selected]="pending.role === role">{{ role }}</option>
                                        }
                                      </select>
                                    </td>
                                    <td>{{ detailData.ccy || '-' }}</td>
                                    <td class="text-center">
                                      <span class="inline-flex items-center gap-2">
                                        <button
                                          type="button"
                                          class="kt-btn kt-btn-sm kt-btn-icon kt-btn-primary"
                                          [disabled]="!pending.role || pending.saving || isSavingPendingLeads() || isMutatingLead()"
                                          (click)="savePendingLead(pending)"
                                          aria-label="Save lead"
                                          [title]="pending.role ? 'Save' : 'Select a Role to save'"
                                        >
                                          <i class="ki-filled ki-check" aria-hidden="true"></i>
                                        </button>
                                        <button
                                          type="button"
                                          class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                                          [disabled]="pending.saving || isSavingPendingLeads()"
                                          (click)="removePendingLead(pending.customerId)"
                                          aria-label="Remove unsaved lead"
                                          title="Remove"
                                        >
                                          <i class="ki-filled ki-cross" aria-hidden="true"></i>
                                        </button>
                                      </span>
                                    </td>
                                  </tr>
                                }
                                @for (lead of detailData.tradeLead; track trackTradeLead($index, lead)) {
                                  @if (isInlineEditingLead(lead)) {
                                    <tr class="bond-lead-inline-row">
                                      <td>{{ lead.customerName || '-' }}</td>
                                      <td>
                                        <select
                                          class="kt-select kt-select-sm bond-lead-inline-role"
                                          aria-label="Role"
                                          [value]="inlineLeadDraft().role"
                                          [disabled]="isMutatingLead() || isLeadRoleLocked()"
                                          (change)="onInlineLeadRoleChange($any($event.target).value)"
                                        >
                                          @if (!isLeadRoleLocked()) {
                                            <option value="" [selected]="inlineLeadDraft().role === ''">Select role</option>
                                          }
                                          @for (role of leadRoleOptions(); track role) {
                                            <option [value]="role" [selected]="inlineLeadDraft().role === role">{{ role }}</option>
                                          }
                                        </select>
                                      </td>
                                      <td>{{ detailData.ccy || '-' }}</td>
                                      <td class="text-center">
                                        <span class="inline-flex items-center gap-2">
                                          <button
                                            type="button"
                                            class="kt-btn kt-btn-sm kt-btn-icon kt-btn-primary"
                                            [disabled]="isMutatingLead()"
                                            (click)="saveInlineLeadEdit(lead)"
                                            aria-label="Save lead changes"
                                            title="Save"
                                          >
                                            <i class="ki-filled ki-check" aria-hidden="true"></i>
                                          </button>
                                          <button
                                            type="button"
                                            class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                                            [disabled]="isMutatingLead()"
                                            (click)="cancelInlineLeadEdit()"
                                            aria-label="Cancel lead changes"
                                            title="Cancel"
                                          >
                                            <i class="ki-filled ki-cross" aria-hidden="true"></i>
                                          </button>
                                        </span>
                                      </td>
                                    </tr>
                                  } @else {
                                    <tr>
                                      <td>{{ lead.customerName || '-' }}</td>
                                      <td>{{ leadRoleLabel(lead.role) }}</td>
                                      <td>{{ detailData.ccy || '-' }}</td>
                                      <td class="text-center">
                                        <span class="inline-flex items-center gap-2">
                                          <button
                                            type="button"
                                            class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                                            [disabled]="isMutatingLead() || inlineEditingLeadCustomerId() !== null"
                                            (click)="startInlineLeadEdit(lead)"
                                            aria-label="Edit role"
                                            title="Quick edit"
                                          >
                                            <i class="ki-filled ki-pencil" aria-hidden="true"></i>
                                          </button>
                                          <button
                                            type="button"
                                            class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                                            data-kt-modal-toggle="#bond_detail_add_lead_modal"
                                            [disabled]="isMutatingLead() || inlineEditingLeadCustomerId() !== null"
                                            (click)="startEditLeadRow(lead)"
                                            aria-label="Open full edit"
                                            title="Full edit (includes fee)"
                                          >
                                            <i class="ki-filled ki-setting-2" aria-hidden="true"></i>
                                          </button>
                                          <button
                                            type="button"
                                            class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                                            [disabled]="isMutatingLead() || inlineEditingLeadCustomerId() !== null"
                                            (click)="deleteLeadRow(lead)"
                                            aria-label="Delete lead row"
                                            title="Delete"
                                          >
                                            <i class="ki-filled ki-trash" aria-hidden="true"></i>
                                          </button>
                                        </span>
                                      </td>
                                    </tr>
                                  }
                                }
                              </tbody>
                            </table>
                          </div>
                        }
                      </div>
                    } @else if (section.id === 'non-named-dealers') {
                      <div class="bond-tab-stack">
                        <div class="flex items-center justify-between gap-2">
                          <h2 class="text-base font-semibold">Non Named Dealers</h2>
                          <button
                            type="button"
                            class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                            data-kt-modal-toggle="#bond_detail_non_named_dealers_modal"
                            (click)="prepareNonNamedDealersModal()"
                            aria-label="Manage Non Named Dealers"
                            title="Add / edit non named dealers"
                          >
                            <i class="ki-filled ki-pencil" aria-hidden="true"></i>
                          </button>
                        </div>

                        @if (detailData.nonNamedDealers.length === 0) {
                          <p class="bond-empty-state">No non named dealers are associated with this trade.</p>
                        } @else {
                          <div class="kt-table-wrapper kt-scrollable">
                            <table class="kt-table">
                              <thead>
                                <tr>
                                  <th>Name</th>
                                </tr>
                              </thead>
                              <tbody>
                                @for (dealer of detailData.nonNamedDealers; track trackNonNamedDealer($index, dealer)) {
                                  <tr>
                                    <td>{{ dealer.name || '-' }}</td>
                                  </tr>
                                }
                              </tbody>
                            </table>
                          </div>
                        }
                      </div>
                    } @else if (section.id === 'documentation') {
                      <div class="bond-tab-stack">
                        <div class="flex items-start justify-between gap-2 flex-wrap">
                          <h2 class="text-base font-semibold">Attached Documentation</h2>

                          <div class="inline-flex items-center gap-4 flex-wrap">
                            @if (detailData.marketType.trim().toUpperCase() !== 'REPURC') {
                              <label class="kt-label flex items-center gap-2 cursor-pointer" [class.bond-doc-flag-pending]="pendingItemFlags().legalDocument">
                                <input type="checkbox" class="kt-checkbox bond-doc-checkbox" [checked]="detailData.hasLegalDocument" [disabled]="isSaving()" (change)="onDocumentationFlagChange('hasLegalDocument', $any($event.target).checked)" />
                                <span>Legal Document</span>
                              </label>
                            }

                            <label class="kt-label flex items-center gap-2 cursor-pointer" [class.bond-doc-flag-pending]="pendingItemFlags().termSheet">
                              <input type="checkbox" class="kt-checkbox bond-doc-checkbox" [checked]="detailData.hasTermSheet" [disabled]="isSaving()" (change)="onDocumentationFlagChange('hasTermSheet', $any($event.target).checked)" />
                              <span>Term Sheet</span>
                            </label>

                            <label class="kt-label flex items-center gap-2 cursor-pointer" [class.bond-doc-flag-pending]="pendingItemFlags().rta">
                              <input type="checkbox" class="kt-checkbox bond-doc-checkbox" [checked]="detailData.hasRta" [disabled]="isSaving()" (change)="onDocumentationFlagChange('hasRta', $any($event.target).checked)" />
                              <span>RTA</span>
                            </label>
                          </div>
                        </div>

                        <div class="flex justify-end">
                          <button
                            type="button"
                            class="kt-btn kt-btn-sm kt-btn-outline"
                            [disabled]="isMutatingDocument()"
                            data-kt-modal-toggle="#bond_detail_add_document_modal"
                            (click)="resetAddDocumentForm()"
                          >
                            <i class="ki-filled ki-plus" aria-hidden="true"></i>
                            <span>Add Documentation</span>
                          </button>
                        </div>

                        @if (detailData.documentsAttached.length === 0) {
                          <p class="bond-empty-state">No attached documentation was found for this bond.</p>
                        } @else {
                          <div class="kt-table-wrapper kt-scrollable">
                            <table class="kt-table">
                              <thead>
                                <tr>
                                  <th>Description</th>
                                  <th>Type</th>
                                  <th>Filename</th>
                                  <th class="text-center">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                @for (document of detailData.documentsAttached; track trackDocument($index, document)) {
                                  <tr>
                                    <td>
                                      <app-document-description-cell
                                        [description]="document.description"
                                        [documentLabel]="document.filename"
                                        [disabled]="isMutatingDocument()"
                                        [saving]="describingDocumentId() === document.id"
                                        (descriptionChange)="updateDocumentDescription(document, $event)"
                                      />
                                    </td>
                                    <td>{{ document.type || '-' }}</td>
                                    <td>
                                      <app-document-name-cell
                                        [filename]="document.filename"
                                        [link]="document.ezShareLink"
                                        [disabled]="isMutatingDocument()"
                                        [saving]="renamingDocumentId() === document.id"
                                        [siblingNames]="documentFilenames(detailData.documentsAttached)"
                                        (rename)="renameDocument(document, $event)"
                                      />
                                    </td>
                                    <td class="text-center">
                                      <span class="inline-flex items-center gap-2">
                                        <button
                                          type="button"
                                          class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                                          [disabled]="isMutatingDocument()"
                                          data-kt-modal-toggle="#bond_detail_add_document_modal"
                                          (click)="startEditDocumentRow(document)"
                                          aria-label="Edit documentation row"
                                        >
                                          <i class="ki-filled ki-pencil" aria-hidden="true"></i>
                                        </button>
                                        <button
                                          type="button"
                                          class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                                          [disabled]="isMutatingDocument()"
                                          data-kt-modal-toggle="#bond_detail_delete_document_modal"
                                          (click)="requestDeleteDocumentRow(document)"
                                          aria-label="Delete documentation row"
                                        >
                                          <i class="ki-filled ki-trash" aria-hidden="true"></i>
                                        </button>
                                      </span>
                                    </td>
                                  </tr>
                                }
                              </tbody>
                            </table>
                          </div>
                        }
                      </div>
                    } @else if (section.id === 'notes') {
                      <div class="bond-tab-stack">
                        <div class="flex items-center justify-between gap-2">
                          <h2 class="text-base font-semibold">Notes</h2>
                          <button
                            type="button"
                            class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                            data-kt-modal-toggle="#bond_detail_add_note_modal"
                            (click)="resetAddNoteForm()"
                            aria-label="Add note"
                          >
                            <i class="ki-filled ki-plus" aria-hidden="true"></i>
                          </button>
                        </div>

                        @if (detailData.notes.length === 0) {
                          <p class="bond-empty-state">No notes were found for this bond.</p>
                        } @else {
                          <div class="kt-table-wrapper kt-scrollable">
                            <table class="kt-table">
                              <thead>
                                <tr>
                                  <th>Note</th>
                                  <th>Author</th>
                                  <th>Date Modified</th>
                                  <th class="text-center">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                @for (note of detailData.notes; track trackNote($index, note)) {
                                  <tr>
                                    <td>{{ note.note || '-' }}</td>
                                    <td>{{ note.author || '-' }}</td>
                                    <td>{{ formatDateTime(note.createdAt) }}</td>
                                    <td class="text-center">
                                      <span class="inline-flex items-center gap-2">
                                        <button
                                          type="button"
                                          class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                                          data-kt-modal-toggle="#bond_detail_add_note_modal"
                                          [disabled]="isMutatingNote()"
                                          (click)="startEditNoteRow(note)"
                                          aria-label="Edit note"
                                        >
                                          <i class="ki-filled ki-pencil" aria-hidden="true"></i>
                                        </button>
                                        <button
                                          type="button"
                                          class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                                          [disabled]="isMutatingNote()"
                                          (click)="requestDeleteNoteRow(note)"
                                          aria-label="Delete note"
                                        >
                                          <i class="ki-filled ki-trash" aria-hidden="true"></i>
                                        </button>
                                      </span>
                                    </td>
                                  </tr>
                                }
                              </tbody>
                            </table>
                          </div>
                        }
                      </div>
                    }
                  </section>
                }
              </div>
            </section>
          }
        }
        }
      </div>
    </div>
  </div>
</main>

<app-catalog-crud-modal
  modalId="bond_detail_save_modal"
  title="Save Bond Detail"
  [submitLabel]="saveButtonLabel()"
  [submitting]="isSaving()"
  [formError]="saveError()"
  (submit)="confirmSave()"
  (cancel)="onSaveModalCancel()"
>
  <div modal-body>
    <div class="rounded-lg border border-input p-3">
      <div class="inline-flex items-start gap-3">
        <i class="ki-filled ki-document" aria-hidden="true"></i>
        <div>
          <p class="text-sm font-semibold">Confirm your changes</p>
          <p class="text-sm text-secondary-foreground mt-1">
            {{ saveIntent() === 'save'
              ? 'Your updated bond details will be saved and remain open for further edits.'
              : 'Your updated bond details will be saved and you will return to the Bonds list.' }}
          </p>
        </div>
      </div>
    </div>
  </div>
</app-catalog-crud-modal>

<app-catalog-crud-modal
  modalId="bond_detail_add_bmk_modal"
  [title]="bmkModalTitle()"
  [submitLabel]="bmkSubmitLabel()"
  [submitting]="isMutatingBmk()"
  [formError]="addFormError()"
  (submit)="addBmkRow()"
  (cancel)="resetAddBmkForm()"
>
  <div modal-body>
    <div class="grid gap-3">
      <div class="grid gap-1.5">
        <label class="kt-form-label" for="add-bmk-trade-id">Trade ID</label>
        <input
          id="add-bmk-trade-id"
          type="text"
          class="kt-input"
          [value]="tradeId"
          disabled=""
        />
      </div>

      <div class="grid gap-1.5">
        <label class="kt-form-label" for="add-bmk-benchmark">Reoffer BMK Name</label>
        <input
          id="add-bmk-benchmark"
          type="text"
          class="kt-input"
          [value]="selectedReofferBmkName() || '-'"
          disabled=""
        />
      </div>

      <div class="grid gap-1.5">
        <label class="kt-form-label" for="add-bmk-value">Spread over Benchmark in bps</label>
        <input
          id="add-bmk-value"
          type="text"
          class="kt-input"
          [value]="addBmkForm().value"
          (input)="updateAddBmkField('value', $any($event.target).value)"
        />
      </div>

      @if (editingBmkId() !== null) {
        <p class="bond-field-hint">Editing mode: Reoffer BMK selection is locked and only spread value can be updated.</p>
      }

      <div class="grid gap-2">

        @if (editingBmkId() === null) {
          <div class="bond-investor-modal-search">
            <i class="ki-filled ki-magnifier" aria-hidden="true"></i>
            <input
              class="kt-input"
              type="text"
              placeholder="Search reoffers..."
              [value]="reofferBmkSearchTerm()"
              (input)="updateReofferBmkSearchTerm($any($event.target).value)"
            />
          </div>

          @if (isLoadingAvailableReofferBmks()) {
            <div class="catalog-loading" role="status" aria-live="polite" aria-label="Loading reoffers">
              <span class="catalog-loading-spinner" aria-hidden="true"></span>
              <span class="catalog-loading-text">Loading reoffers...</span>
            </div>
          } @else if (filteredReofferBmks().length === 0) {
            <p class="bond-empty-state">No reoffers are available.</p>
          } @else {
            <div class="kt-table-wrapper kt-scrollable max-h-64">
              <table class="kt-table">
                <thead>
                  <tr>
                    <th>Reoffer</th>
                    <th class="text-center">Select</th>
                  </tr>
                </thead>
                <tbody>
                  @for (reoffer of filteredReofferBmks(); track trackAvailableReofferBmk($index, reoffer)) {
                    <tr>
                      <td>{{ reoffer.name || '-' }}</td>
                      <td class="text-center">
                        <input
                          type="radio"
                          name="selectedBmkReoffer"
                          [checked]="isReofferBmkSelected(reoffer.id)"
                          [disabled]="isMutatingBmk()"
                          (change)="selectReofferBmk(reoffer)"
                          [attr.aria-label]="'Select reoffer ' + (reoffer.name || '-')"
                        />
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
        }
      </div>
    </div>
  </div>
</app-catalog-crud-modal>

<app-catalog-crud-modal
  modalId="bond_detail_add_investors_modal"
  title="Add Investors & Allocations"
  submitLabel=""
  cancelLabel="Close"
  [showSubmit]="false"
  [submitting]="false"
  [formError]="addInvestorsError()"
  widthClass="max-w-[1280px]"
  (cancel)="resetAddInvestorsModal()"
>
  <div modal-body>
    <div class="bond-investor-modal-summary">
      <div class="bond-investor-modal-trade-field">
        <label class="kt-form-label" for="bond-investor-modal-trade-id">Trade ID</label>
        <input
          id="bond-investor-modal-trade-id"
          class="kt-input"
          type="text"
          [value]="tradeId"
          readonly
        />
      </div>

      @if (detail(); as detailData) {
        <div class="bond-investor-modal-stats">
          <div class="bond-investor-modal-stat-line">
            <span class="bond-investor-modal-stat-label">Notional Issued:</span>
            <span class="bond-investor-modal-stat-value">
              {{ formatCurrencyWithCode(detailData.notional, detailData.ccy) }}
            </span>
          </div>
          <div class="bond-investor-modal-stat-line">
            <span class="bond-investor-modal-stat-label">Allocated to Investors:</span>
            <span class="bond-investor-modal-stat-value">
              {{ formatCurrencyWithCode(totalInvestorAllocation(), detailData.ccy) }}
            </span>
          </div>
          <div class="bond-investor-modal-stat-line">
            <span class="bond-investor-modal-stat-label">Status:</span>
            <span class="bond-investor-modal-stat-value" [class]="investorAllocationStatusClass()">
              {{ investorAllocationStatusLabel() }}
            </span>
          </div>
        </div>
      }
    </div>

    <div class="kt-tabs kt-tabs-line bond-investor-modal-tabs" role="tablist" aria-label="Investor modal tabs">
      <button
        type="button"
        role="tab"
        class="kt-tab-toggle"
        [class.active]="investorModalActiveTab() === 'select-allocate'"
        [attr.aria-selected]="investorModalActiveTab() === 'select-allocate'"
        (click)="activateInvestorsModalTab('select-allocate')"
      >
        <i class="ki-filled ki-user-tick" aria-hidden="true"></i>
        Select & Allocate
      </button>
      <button
        type="button"
        role="tab"
        class="kt-tab-toggle"
        [class.active]="investorModalActiveTab() === 'create-investor'"
        [attr.aria-selected]="investorModalActiveTab() === 'create-investor'"
        (click)="activateInvestorsModalTab('create-investor')"
      >
        <i class="ki-filled ki-user-edit" aria-hidden="true"></i>
        Create Investor
      </button>
      <button
        type="button"
        role="tab"
        class="kt-tab-toggle"
        [class.active]="investorModalActiveTab() === 'create-investor-group'"
        [attr.aria-selected]="investorModalActiveTab() === 'create-investor-group'"
        (click)="activateInvestorsModalTab('create-investor-group')"
      >
        <i class="ki-filled ki-people" aria-hidden="true"></i>
        Create Investor Group
      </button>
    </div>

    @if (investorModalActiveTab() === 'select-allocate') {
      @if (isLoadingAvailableInvestors()) {
        <div class="catalog-loading" role="status" aria-live="polite" aria-label="Loading investors">
          <span class="catalog-loading-spinner" aria-hidden="true"></span>
          <span class="catalog-loading-text">Loading investors...</span>
        </div>
      } @else if (investorModalRows().length === 0) {
        <p class="bond-empty-state">No investors are available.</p>
      } @else {
        <div class="bond-investor-modal-toolbar">
          <div class="bond-investor-modal-search">
            <i class="ki-filled ki-magnifier" aria-hidden="true"></i>
            <input
              class="kt-input"
              type="text"
              placeholder="Search investors..."
              [value]="investorModalSearchTerm()"
              (input)="updateInvestorModalSearchTerm($any($event.target).value)"
            />
          </div>
        </div>

        <div class="bond-investor-picker-table-wrapper kt-scrollable">
          <table class="kt-table">
            <thead>
              <tr>
                <th>Investor</th>
                <th>Group</th>
                <th>Type</th>
                <th>Region</th>
                <th>Country</th>
                <th class="text-end">Allocation</th>
                <th class="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              @if (pagedInvestorModalRows().length === 0) {
                <tr>
                  <td colspan="7" class="text-center text-secondary-foreground py-6">
                    No investors matched your search.
                  </td>
                </tr>
              } @else {
              @for (investor of pagedInvestorModalRows(); track trackAvailableInvestor($index, investor)) {
                <tr>
                  <td>{{ investor.name || '-' }}</td>
                  <td>{{ investor.investorGroupName || '-' }}</td>
                  <td>{{ investor.investorTypeName || '-' }}</td>
                  <td>{{ investor.region || '-' }}</td>
                  <td>{{ investor.country || '-' }}</td>
                  <td class="text-end">
                    @if (isInvestorAllocationEditing(investor.id)) {
                      <input
                        class="kt-input kt-input-sm bond-investor-allocation-input"
                        type="text"
                        inputmode="decimal"
                        [value]="editingInvestorAllocationValue()"
                        (input)="updateInvestorAllocationValue($any($event.target).value)"
                        (keydown)="onInvestorAllocationInputKeydown($event)"
                        placeholder="Enter allocation"
                      />
                    } @else {
                      {{ formatCurrencyLike(investor.currentAllocation) }}
                    }
                  </td>
                  <td class="text-center">
                    @if (isInvestorAllocationEditing(investor.id)) {
                      <span class="inline-flex items-center gap-2">
                        <button
                          type="button"
                          class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                          aria-label="Cancel allocation edit"
                          [disabled]="isMutatingInvestor()"
                          (click)="cancelInvestorAllocationEdit(investor.id)"
                        >
                          <i class="ki-filled ki-cross" aria-hidden="true"></i>
                        </button>
                        <button
                          type="button"
                          class="kt-btn kt-btn-sm kt-btn-icon"
                          aria-label="Save investor allocation"
                          [disabled]="isMutatingInvestor()"
                          (click)="saveInvestorAllocationRow(investor)"
                        >
                          <i class="ki-filled ki-check" aria-hidden="true"></i>
                        </button>
                      </span>
                    } @else if (investor.isAdded) {
                      <span class="inline-flex items-center gap-2">
                        <button
                          type="button"
                          class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                          aria-label="Edit investor allocation"
                          [disabled]="isMutatingInvestor()"
                          (click)="startInvestorAllocationEdit(investor)"
                        >
                          <i class="ki-filled ki-pencil" aria-hidden="true"></i>
                        </button>
                        <button
                          type="button"
                          class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                          aria-label="Delete investor from bond"
                          [disabled]="isMutatingInvestor()"
                          (click)="deleteInvestorFromModal(investor)"
                        >
                          <i class="ki-filled ki-trash" aria-hidden="true"></i>
                        </button>
                      </span>
                    } @else {
                      <button
                        type="button"
                        class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline"
                        aria-label="Add investor allocation"
                        [disabled]="isMutatingInvestor()"
                        (click)="startInvestorAllocationEdit(investor)"
                      >
                        <i class="ki-filled ki-plus" aria-hidden="true"></i>
                      </button>
                    }
                  </td>
                </tr>
              }
              }
            </tbody>
          </table>
        </div>

        <div class="bond-investor-modal-pagination">
          <div class="text-sm text-secondary-foreground">
            Showing {{ investorModalRangeStart() }} - {{ investorModalRangeEnd() }} of {{ filteredInvestorModalRows().length }}
          </div>

          <div class="bond-investor-modal-pagination-controls">
            <label class="text-sm text-secondary-foreground" for="bond-investor-modal-page-size">Rows:</label>
            <select
              id="bond-investor-modal-page-size"
              class="kt-select kt-select-sm w-20"
              [value]="investorModalPageSize()"
              (change)="updateInvestorModalPageSize($any($event.target).value)"
            >
              @for (size of investorModalPageSizeOptions; track size) {
                <option [value]="size">{{ size }}</option>
              }
            </select>

            <button
              type="button"
              class="kt-btn kt-btn-sm kt-btn-light"
              [disabled]="!hasPreviousInvestorModalPage()"
              (click)="goToPreviousInvestorModalPage()"
            >
              Previous
            </button>

            <span class="text-sm text-secondary-foreground">
              Page {{ investorModalCurrentPage() }} of {{ investorModalTotalPages() }}
            </span>

            <button
              type="button"
              class="kt-btn kt-btn-sm kt-btn-light"
              [disabled]="!hasNextInvestorModalPage()"
              (click)="goToNextInvestorModalPage()"
            >
              Next
            </button>
          </div>
        </div>
      }
    } @else if (investorModalActiveTab() === 'create-investor') {
      @if (isLoadingInvestorCreateLookups()) {
        <div class="catalog-loading" role="status" aria-live="polite" aria-label="Loading investor creation lookups">
          <span class="catalog-loading-spinner" aria-hidden="true"></span>
          <span class="catalog-loading-text">Loading lookup data...</span>
        </div>
      } @else {
        @if (investorCreateLookupError()) {
          <div class="kt-alert kt-alert-destructive" role="alert">
            <div class="kt-alert-text">{{ investorCreateLookupError() }}</div>
          </div>
        }

        <div class="bond-investor-inline-form-grid">
          <div class="grid gap-1.5">
            <label class="kt-form-label" for="bond-create-investor-name">Name <span class="catalog-required-marker">*</span></label>
            <input
              id="bond-create-investor-name"
              type="text"
              class="kt-input"
              [value]="createInvestorForm().name"
              (input)="updateCreateInvestorField('name', $any($event.target).value)"
            />
          </div>

          <div class="grid gap-1.5">
            <label class="kt-form-label" for="bond-create-investor-type">Investor Type <span class="catalog-required-marker">*</span></label>
            <select
              id="bond-create-investor-type"
              class="kt-select"
              [value]="createInvestorForm().investorTypeId"
              (change)="updateCreateInvestorField('investorTypeId', $any($event.target).value)"
            >
              <option value="">Select investor type...</option>
              @for (item of investorModalInvestorTypes(); track item.id) {
                <option [value]="item.id">{{ item.name }}</option>
              }
            </select>
          </div>

          <div class="grid gap-1.5">
            <label class="kt-form-label" for="bond-create-investor-country">Country <span class="catalog-required-marker">*</span></label>
            <select
              id="bond-create-investor-country"
              class="kt-select"
              data-kt-select="true"
              data-kt-select-enable-search="true"
              data-kt-select-search-placeholder="Search..."
              data-kt-select-placeholder="Select a country..."
              data-kt-select-config='{"optionsClass":"kt-scrollable overflow-auto max-h-[250px]"}'
              [value]="createInvestorForm().countryId"
              (change)="updateCreateInvestorField('countryId', $any($event.target).value)"
            >
              <option value="">Select a country...</option>
              @for (item of investorModalCountries(); track item.id) {
                <option [value]="item.id">{{ item.name }}</option>
              }
            </select>
          </div>

          <div class="grid gap-1.5">
            <label class="kt-form-label" for="bond-create-investor-region">Region <span class="catalog-required-marker">*</span></label>
            <select
              id="bond-create-investor-region"
              class="kt-select"
              [value]="createInvestorForm().regionId"
              (change)="updateCreateInvestorField('regionId', $any($event.target).value)"
            >
              <option value="">Select region...</option>
              @for (item of investorModalRegions(); track item.id) {
                <option [value]="item.id">{{ item.name }}</option>
              }
            </select>
          </div>

          <div class="grid gap-1.5">
            <label class="kt-form-label" for="bond-create-investor-group">Investor Group <span class="catalog-required-marker">*</span></label>
            <select
              id="bond-create-investor-group"
              class="kt-select"
              data-kt-select="true"
              data-kt-select-enable-search="true"
              data-kt-select-search-placeholder="Search..."
              data-kt-select-placeholder="Select an investor group..."
              data-kt-select-config='{"optionsClass":"kt-scrollable overflow-auto max-h-[250px]"}'
              [value]="createInvestorForm().investorGroupId"
              (change)="updateCreateInvestorField('investorGroupId', $any($event.target).value)"
            >
              <option value="">Select investor group...</option>
              @for (item of investorModalInvestorGroups(); track item.id) {
                <option [value]="item.id">{{ item.name }}</option>
              }
            </select>
          </div>
        </div>

        @if (createInvestorError()) {
          <div class="kt-alert kt-alert-destructive mt-3" role="alert">
            <div class="kt-alert-text">{{ createInvestorError() }}</div>
          </div>
        }

        <div class="bond-investor-inline-actions">
          <button
            type="button"
            class="kt-btn kt-btn-primary"
            [disabled]="isCreatingInvestor() || isCreatingInvestorGroup()"
            (click)="createInvestorFromModal()"
          >
            <i class="ki-filled ki-plus" aria-hidden="true"></i>
            @if (!isCreatingInvestor()) {
              <span>Create Investor</span>
            } @else {
              <span>Creating...</span>
            }
          </button>
        </div>
      }
    } @else {
      @if (isLoadingInvestorCreateLookups()) {
        <div class="catalog-loading" role="status" aria-live="polite" aria-label="Loading investor group creation lookups">
          <span class="catalog-loading-spinner" aria-hidden="true"></span>
          <span class="catalog-loading-text">Loading lookup data...</span>
        </div>
      } @else {
        @if (investorCreateLookupError()) {
          <div class="kt-alert kt-alert-destructive" role="alert">
            <div class="kt-alert-text">{{ investorCreateLookupError() }}</div>
          </div>
        }

        <div class="bond-investor-inline-form-grid">
          <div class="grid gap-1.5">
            <label class="kt-form-label" for="bond-create-investor-group-name">Name <span class="catalog-required-marker">*</span></label>
            <input
              id="bond-create-investor-group-name"
              type="text"
              class="kt-input"
              maxlength="60"
              [value]="createInvestorGroupForm().name"
              (input)="updateCreateInvestorGroupField('name', $any($event.target).value)"
            />
          </div>

          <div class="grid gap-1.5">
            <label class="kt-form-label" for="bond-create-investor-group-country">Country</label>
            <select
              id="bond-create-investor-group-country"
              class="kt-select"
              data-kt-select="true"
              data-kt-select-enable-search="true"
              data-kt-select-search-placeholder="Search..."
              data-kt-select-placeholder="Select a country..."
              data-kt-select-config='{"optionsClass":"kt-scrollable overflow-auto max-h-[250px]"}'
              [value]="createInvestorGroupForm().hqCountryId"
              (change)="updateCreateInvestorGroupField('hqCountryId', $any($event.target).value)"
            >
              <option value="">Select a country...</option>
              @for (item of investorModalCountries(); track item.id) {
                <option [value]="item.id">{{ item.name }}</option>
              }
            </select>
          </div>

          <div class="grid gap-1.5">
            <label class="kt-form-label" for="bond-create-investor-group-region">Region</label>
            <select
              id="bond-create-investor-group-region"
              class="kt-select"
              [value]="createInvestorGroupForm().regionId"
              (change)="updateCreateInvestorGroupField('regionId', $any($event.target).value)"
            >
              <option value="">Select region...</option>
              @for (item of investorModalRegions(); track item.id) {
                <option [value]="item.id">{{ item.name }}</option>
              }
            </select>
          </div>
        </div>

        @if (createInvestorGroupError()) {
          <div class="kt-alert kt-alert-destructive mt-3" role="alert">
            <div class="kt-alert-text">{{ createInvestorGroupError() }}</div>
          </div>
        }

        <div class="bond-investor-inline-actions">
          <button
            type="button"
            class="kt-btn kt-btn-primary"
            [disabled]="isCreatingInvestorGroup() || isCreatingInvestor()"
            (click)="createInvestorGroupFromModal()"
          >
            <i class="ki-filled ki-plus" aria-hidden="true"></i>
            @if (!isCreatingInvestorGroup()) {
              <span>Create Investor Group</span>
            } @else {
              <span>Creating...</span>
            }
          </button>
        </div>
      }
    }
  </div>
</app-catalog-crud-modal>

<app-catalog-crud-modal
  modalId="bond_detail_add_lead_modal"
  [title]="leadModalTitle()"
  [submitLabel]="leadSubmitLabel()"
  [submitting]="isMutatingLead()"
  [formError]="addFormError()"
  widthClass="max-w-[1200px]"
  (submit)="addLeadOrDealerRow()"
  (cancel)="resetAddLeadForm()"
>
  <div modal-body>
    <div class="grid gap-4">
      @if (detail(); as detailData) {
        @if (!isCdnSecurity()) {
          <div class="grid gap-1.5">
            <div class="flex items-center justify-between gap-2">
              <span class="kt-form-label" id="add-lead-bank-names-label">
                {{ editingLeadRowKey() === null ? 'Bank Names' : 'Bank Name' }}
              </span>
              @if (editingLeadRowKey() === null && selectedLeadCustomers().length > 0) {
                <button type="button" class="kt-link kt-link-underlined kt-link-dashed text-xs" (click)="clearSelectedLeadCustomers()">
                  Clear selection
                </button>
              }
            </div>
            @if (editingLeadRowKey() === null) {
              <div class="bond-lead-selected-banks" role="list" aria-labelledby="add-lead-bank-names-label">
                @for (selection of selectedLeadCustomers(); track selection.id) {
                  <span class="bond-lead-bank-chip" role="listitem">
                    <span class="bond-lead-bank-chip-name">{{ selection.legalName }}</span>
                    <span class="bond-lead-bank-chip-id">{{ selection.id }}</span>
                    <button
                      type="button"
                      class="bond-lead-bank-chip-remove"
                      [disabled]="isMutatingLead()"
                      (click)="removeSelectedLeadCustomer(selection.id)"
                      [attr.aria-label]="'Remove ' + selection.legalName"
                    >
                      <i class="ki-filled ki-cross" aria-hidden="true"></i>
                    </button>
                  </span>
                } @empty {
                  <span class="bond-lead-selected-banks-empty">Select one or more banks from the list below.</span>
                }
              </div>
            } @else {
              <input
                id="add-lead-customer-name"
                type="text"
                class="kt-input"
                aria-labelledby="add-lead-bank-names-label"
                [value]="addLeadForm().customerName"
                disabled=""
              />
            }
          </div>
        }

        <div class="bond-lead-modal-top-grid">
          <div class="grid gap-1.5">
            <label class="kt-form-label" for="add-lead-trade-id">Trade ID</label>
            <input
              id="add-lead-trade-id"
              type="text"
              class="kt-input"
              [value]="detailData.tradeId"
              disabled=""
            />
          </div>

          <div class="grid gap-1.5">
            <label class="kt-form-label" for="add-lead-currency">Currency</label>
            <input
              id="add-lead-currency"
              type="text"
              class="kt-input"
              [value]="detailData.ccy || '-'"
              disabled=""
            />
          </div>

          @if (!isCdnSecurity()) {
            <div class="grid gap-1.5">
              <label class="kt-form-label" for="add-lead-notional">Notional</label>
              <input
                id="add-lead-notional"
                type="text"
                class="kt-input"
                [value]="formatCurrencyLike(detailData.notional)"
                disabled=""
              />
            </div>
          } @else {
            <div class="grid gap-1.5">
              <label class="kt-form-label" for="add-lead-role">Role</label>
              <input id="add-lead-role" type="text" class="kt-input" [value]="addLeadForm().role || 'LEAD'" disabled="" />
            </div>
          }
        </div>
      }

      @if (!isCdnSecurity()) {
        @if (editingLeadRowKey() !== null) {
          <div class="bond-lead-bank-notional-row">
            <div class="grid gap-1.5">
              <label class="kt-form-label" for="add-lead-role">Role</label>
              <select
                id="add-lead-role"
                class="kt-select"
                [value]="addLeadForm().role"
                (change)="onLeadRoleChange($any($event.target).value)"
                [disabled]="isLeadRoleLocked()"
              >
                @if (!isLeadRoleLocked()) {
                  <option value="" [selected]="addLeadForm().role === ''">Select role</option>
                }
                @for (role of leadRoleOptions(); track role) {
                  <option [value]="role" [selected]="addLeadForm().role === role">{{ role }}</option>
                }
              </select>
            </div>

            <div class="grid gap-1.5">
              <label class="kt-form-label" for="add-lead-arb">Arbitrage Notional</label>
              <input
                id="add-lead-arb"
                type="number"
                step="any"
                class="kt-input"
                [value]="addLeadForm().arbitrageNotional"
                (input)="updateAddLeadField('arbitrageNotional', $any($event.target).value)"
                [disabled]="isArbitrageNotionalDisabled()"
              />
            </div>
          </div>

          <div class="bond-lead-warning-block">
            <span>Warning:</span>
            <span>Co-Lead Default: 1% from Bond Fee per Co-Lead</span>
            <span>Lead Default: Remaining Fee Amount split evenly among all Leads</span>
          </div>

          <label class="kt-label flex items-center gap-2">
            <input
              type="checkbox"
              [checked]="addLeadForm().useCustomFee"
              (change)="onLeadUseCustomFeeChange($any($event.target).checked)"
            />
            <span>Select not to use default bond fees</span>
          </label>

          @if (addLeadForm().useCustomFee) {
            <section class="bond-lead-fee-section">
              <div class="bond-lead-fee-row">
                <div class="grid gap-1.5">
                  <label class="kt-form-label" for="lead-fee-ccy">Bond Fee CCY</label>
                  <input
                    id="lead-fee-ccy"
                    class="kt-input"
                    type="text"
                    [value]="detail()?.issFeeCurrency || detail()?.ccy || '-'"
                    disabled=""
                  />
                </div>

                <div class="grid gap-1.5">
                  <label class="kt-form-label" for="lead-fee-amount">Bond Fee Amount</label>
                  <input
                    id="lead-fee-amount"
                    class="kt-input"
                    type="text"
                    [value]="formatCurrencyLike(detail()?.issFee ?? null)"
                    disabled=""
                  />
                </div>

                <div class="grid gap-1.5">
                  <label class="kt-form-label" for="lead-fee-custom">Fee Lead / Co-Lead</label>
                  <input
                    id="lead-fee-custom"
                    class="kt-input"
                    type="number"
                    step="any"
                    [value]="addLeadForm().feeLeadColead"
                    (input)="updateAddLeadField('feeLeadColead', $any($event.target).value)"
                  />
                </div>

                <div class="grid gap-1.5">
                  <label class="kt-form-label" for="lead-fee-percentage">Percentage</label>
                  <input
                    id="lead-fee-percentage"
                    class="kt-input"
                    type="number"
                    step="any"
                    min="0"
                    [value]="addLeadForm().feePercentage"
                    (input)="onLeadFeePercentageChange($any($event.target).value)"
                  />
                </div>
              </div>
            </section>
          }
        } @else {
          <section class="bond-customer-picker" aria-label="Select bank names">
            <header class="bond-customer-picker-header">
              <span>Select Bank Names</span>
              <span class="text-secondary-foreground text-sm font-normal">
                {{ selectedLeadCustomers().length }} selected · {{ groupedCustomers().length }} parents · {{ groupedCustomersCount() }} banks
              </span>
            </header>

            <div class="bond-customer-picker-content">
              <div class="bond-customer-picker-toolbar bond-customer-picker-search">
                <i class="ki-filled ki-magnifier bond-customer-picker-search-icon" aria-hidden="true"></i>
                <input
                  class="kt-input"
                  type="search"
                  placeholder="Search by legal name..."
                  aria-label="Search bank names"
                  [value]="customerSearchTerm()"
                  (input)="updateCustomerSearchTerm($any($event.target).value)"
                  (keydown.enter)="$event.preventDefault(); searchGroupedCustomers()"
                />
              </div>

              @if (isLoadingGroupedCustomers()) {
                <div class="catalog-loading" role="status" aria-live="polite" aria-label="Loading bank names">
                  <span class="catalog-loading-spinner" aria-hidden="true"></span>
                  <span class="catalog-loading-text">Loading bank names...</span>
                </div>
              } @else if (groupedCustomers().length === 0) {
                <p class="bond-empty-state">No bank names were found.</p>
              } @else {
                <div class="kt-table-wrapper kt-scrollable bond-customer-picker-table">
                  <table class="kt-table">
                    <thead>
                      <tr>
                        <th class="bond-customer-select-col"><span class="sr-only">Select</span></th>
                        <th>Legal Name</th>
                        <th>Bank Code (ID)</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (group of groupedCustomers(); track group.parent) {
                        @let selectableGroup = isSelectableCustomerGroup(group);
                        @let groupExpanded = isCustomerGroupExpanded(group);
                        @let groupAdded = selectableGroup && isLeadCustomerAlreadyAdded(group.parent);
                        @let groupSelected = selectableGroup && isLeadCustomerSelected(group.parent);
                        <tr class="bond-customer-group-row" [class.bond-customer-row-selected]="groupSelected">
                          <td class="bond-customer-select-col">
                            @if (selectableGroup) {
                              <input
                                type="checkbox"
                                class="kt-checkbox kt-checkbox-sm bond-doc-checkbox"
                                [checked]="groupSelected || groupAdded"
                                [disabled]="groupAdded || isMutatingLead()"
                                (change)="toggleLeadCustomerGroup(group)"
                                [attr.aria-label]="'Select parent ' + customerGroupLabel(group)"
                              />
                            }
                          </td>
                          <td>
                            <span class="inline-flex items-center gap-2">
                              @if (selectableGroup) {
                                <button
                                  type="button"
                                  class="bond-customer-group-toggle"
                                  (click)="toggleCustomerGroup(group)"
                                  [attr.aria-expanded]="groupExpanded"
                                  [attr.aria-label]="(groupExpanded ? 'Collapse ' : 'Expand ') + customerGroupLabel(group)"
                                >
                                  <i class="ki-filled" [class.ki-down]="groupExpanded" [class.ki-right]="!groupExpanded" aria-hidden="true"></i>
                                </button>
                              }
                              <span>{{ customerGroupLabel(group) }}</span>
                              <span class="kt-badge kt-badge-sm kt-badge-outline">{{ group.customers.length }}</span>
                              @if (groupAdded) {
                                @if (isLeadCustomerPending(group.parent)) {
                                  <span class="kt-badge kt-badge-sm kt-badge-warning kt-badge-outline">Unsaved</span>
                                } @else {
                                  <span class="kt-badge kt-badge-sm kt-badge-success kt-badge-outline">Already added</span>
                                }
                              }
                            </span>
                          </td>
                          <td>{{ selectableGroup ? group.parent : '-' }}</td>
                        </tr>

                        @if (groupExpanded) {
                          @for (customer of group.customers; track customer.id + '-' + customer.legalName) {
                            @let customerAdded = isLeadCustomerAlreadyAdded(customer.id);
                            @let customerSelected = isLeadCustomerSelected(customer.id);
                            <tr class="bond-customer-child-row" [class.bond-customer-row-selected]="customerSelected">
                              <td class="bond-customer-select-col">
                                <input
                                  type="checkbox"
                                  class="kt-checkbox kt-checkbox-sm bond-doc-checkbox"
                                  [checked]="customerSelected || customerAdded"
                                  [disabled]="customerAdded || isMutatingLead()"
                                  (change)="toggleLeadCustomer(customer)"
                                  [attr.aria-label]="'Select ' + (customer.legalName || customer.id)"
                                />
                              </td>
                              <td>
                                <span class="bond-customer-child-name">
                                  {{ customer.legalName || '-' }}
                                  @if (customerAdded) {
                                    @if (isLeadCustomerPending(customer.id)) {
                                      <span class="kt-badge kt-badge-sm kt-badge-warning kt-badge-outline">Unsaved</span>
                                    } @else {
                                      <span class="kt-badge kt-badge-sm kt-badge-success kt-badge-outline">Already added</span>
                                    }
                                  }
                                </span>
                              </td>
                              <td>{{ customer.id || '-' }}</td>
                            </tr>
                          }
                        }
                      }
                    </tbody>
                  </table>
                </div>
              }
            </div>
          </section>
        }
      }
    </div>
  </div>
</app-catalog-crud-modal>

<app-catalog-crud-modal
  modalId="bond_detail_non_named_dealers_modal"
  title="Non Named Dealers"
  submitLabel="Save Dealers"
  [submitting]="isSavingNonNamedDealers()"
  [formError]="nonNamedDealerFormError()"
  widthClass="max-w-[640px]"
  (submit)="saveNonNamedDealers()"
  (cancel)="resetNonNamedDealersModal()"
>
  <div modal-body>
    <section class="bond-customer-picker" aria-label="Select non named dealers">
      <header class="bond-customer-picker-header">
        <span>Select Non Named Dealers</span>
        <span class="text-secondary-foreground text-sm font-normal">
          {{ selectedNonNamedDealerIds().size }} selected · {{ availableNonNamedDealers().length }} dealers
        </span>
      </header>

      <div class="bond-customer-picker-content">
        <div class="bond-customer-picker-toolbar bond-customer-picker-search">
          <i class="ki-filled ki-magnifier bond-customer-picker-search-icon" aria-hidden="true"></i>
          <input
            class="kt-input"
            type="search"
            placeholder="Search by name..."
            aria-label="Search non named dealers"
            [value]="nonNamedDealerSearchTerm()"
            (input)="nonNamedDealerSearchTerm.set($any($event.target).value)"
            (keydown.enter)="$event.preventDefault()"
          />
        </div>

        @if (isLoadingNonNamedDealers()) {
          <div class="catalog-loading" role="status" aria-live="polite" aria-label="Loading non named dealers">
            <span class="catalog-loading-spinner" aria-hidden="true"></span>
            <span class="catalog-loading-text">Loading non named dealers...</span>
          </div>
        } @else if (filteredNonNamedDealers().length === 0) {
          <p class="bond-empty-state">No non named dealers were found.</p>
        } @else {
          <div class="kt-table-wrapper kt-scrollable bond-customer-picker-table">
            <table class="kt-table">
              <thead>
                <tr>
                  <th class="bond-customer-select-col">
                    <input
                      type="checkbox"
                      class="kt-checkbox kt-checkbox-sm bond-doc-checkbox"
                      [checked]="areAllFilteredNonNamedDealersSelected()"
                      [disabled]="isSavingNonNamedDealers()"
                      (change)="toggleAllFilteredNonNamedDealers($any($event.target).checked)"
                      aria-label="Select all listed non named dealers"
                    />
                  </th>
                  <th>Name</th>
                </tr>
              </thead>
              <tbody>
                @for (dealer of filteredNonNamedDealers(); track dealer.id) {
                  @let dealerSelected = isNonNamedDealerSelected(dealer.id);
                  <tr
                    class="bond-customer-child-row cursor-pointer"
                    [class.bond-customer-row-selected]="dealerSelected"
                    (click)="!isSavingNonNamedDealers() && toggleNonNamedDealer(dealer.id)"
                  >
                    <td class="bond-customer-select-col">
                      <input
                        type="checkbox"
                        class="kt-checkbox kt-checkbox-sm bond-doc-checkbox"
                        [checked]="dealerSelected"
                        [disabled]="isSavingNonNamedDealers()"
                        (click)="$event.stopPropagation()"
                        (change)="toggleNonNamedDealer(dealer.id)"
                        [attr.aria-label]="'Select ' + (dealer.name || dealer.id)"
                      />
                    </td>
                    <td>{{ dealer.name || '-' }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </div>
    </section>
  </div>
</app-catalog-crud-modal>

<app-catalog-crud-modal
  modalId="bond_detail_add_document_modal"
  [title]="documentModalTitle()"
  [submitLabel]="documentSubmitLabel()"
  [submitting]="isMutatingDocument()"
  [formError]="addFormError()"
  (submit)="addDocumentRow()"
>
  <div modal-body>
    <div class="grid gap-3">
      @if (editingDocumentId() === null) {
        <div class="grid gap-1.5">
          <label class="kt-form-label" for="add-document-files">Files</label>
          <input
            #documentFilesInput
            id="add-document-files"
            type="file"
            class="kt-input"
            accept=".doc,.msg,.pdf,.xls"
            multiple
            [disabled]="isMutatingDocument()"
            (change)="onDocumentFilesSelected($event)"
          />
          <p class="text-xs text-secondary-foreground">Allowed types: .doc, .msg, .pdf, .xls</p>
        </div>

        @if (documentUploadEntries().length > 0) {
          <div class="grid gap-1">
            <p class="text-sm font-medium">Selected files</p>
            <app-document-upload-list [entries]="documentUploadEntries()" />
          </div>
        }
      } @else {
        <div class="grid gap-1.5">
          <label class="kt-form-label" for="add-document-filename">Filename</label>
          <input
            id="add-document-filename"
            type="text"
            class="kt-input"
            [value]="addDocumentForm().filename"
            (input)="updateAddDocumentField('filename', $any($event.target).value)"
          />
        </div>
        <div class="grid gap-1.5">
          <label class="kt-form-label" for="add-document-description">Description</label>
          <input
            id="add-document-description"
            type="text"
            class="kt-input"
            [value]="addDocumentForm().description"
            (input)="updateAddDocumentField('description', $any($event.target).value)"
          />
        </div>
      }
    </div>
  </div>
</app-catalog-crud-modal>

<app-catalog-delete-confirm-modal
  modalId="bond_detail_delete_document_modal"
  title="Delete Document"
  [message]="'Delete document: ' + (pendingDocumentDeletePreview() || '-') + '?'"
  confirmLabel="Delete"
  [confirming]="isMutatingDocument()"
  (confirm)="confirmDeleteDocumentRow()"
  (cancel)="cancelDeleteDocumentRow()"
></app-catalog-delete-confirm-modal>

<app-catalog-crud-modal
  modalId="bond_detail_add_note_modal"
  [title]="noteModalTitle()"
  [submitLabel]="noteSubmitLabel()"
  [submitting]="isMutatingNote()"
  [formError]="addFormError()"
  (submit)="addNoteRow()"
  (cancel)="resetAddNoteForm()"
>
  <div modal-body>
    <div class="grid gap-1.5">
      <label class="kt-form-label" for="add-note-text">Note</label>
      <textarea
        id="add-note-text"
        class="kt-textarea"
        rows="4"
        [value]="addNoteForm().note"
        (input)="updateAddNoteField('note', $any($event.target).value)"
      ></textarea>
    </div>
  </div>
</app-catalog-crud-modal>

<app-catalog-delete-confirm-modal
  modalId="bond_detail_delete_note_modal"
  title="Delete Note"
  [message]="'Delete note: ' + (pendingNoteDeletePreview() || '-') + '?'"
  confirmLabel="Delete"
  [confirming]="isMutatingNote()"
  (confirm)="confirmDeleteNoteRow()"
  (cancel)="cancelDeleteNoteRow()"
></app-catalog-delete-confirm-modal>

<app-catalog-delete-confirm-modal
  modalId="bond_detail_delete_modal"
  title="Delete Bond"
  message="Are you sure you want to delete this bond?"
  confirmLabel="Delete Bond"
  [confirming]="isDeletingBond()"
  (confirm)="confirmDeleteBond()"
></app-catalog-delete-confirm-modal>

<app-catalog-delete-confirm-modal
  modalId="bond_detail_delete_investor_modal"
  title="Delete Investor"
  [message]="'Delete investor ' + (pendingInvestorDeleteName() || '-') + ' from this bond?'"
  confirmLabel="Delete"
  [confirming]="isMutatingInvestor()"
  (confirm)="confirmDeleteMainInvestor()"
  (cancel)="cancelDeleteMainInvestor()"
></app-catalog-delete-confirm-modal>

<app-catalog-delete-confirm-modal
  modalId="bond_detail_delete_bmk_modal"
  title="Delete Reoffer Row"
  [message]="'Delete reoffer ' + (pendingBmkDeleteName() || '-') + ' from this bond?'"
  confirmLabel="Delete"
  [confirming]="isMutatingBmk()"
  (confirm)="confirmDeleteBmkRow()"
  (cancel)="cancelDeleteBmkRow()"
></app-catalog-delete-confirm-modal>

<app-catalog-delete-confirm-modal
  modalId="bond_detail_discard_changes_modal"
  title="Unsaved Changes"
  message="You have unsaved changes. Do you want to discard them and close?"
  confirmLabel="Discard & Close"
  [confirming]="false"
  (confirm)="discardChangesAndClose()"
></app-catalog-delete-confirm-modal>

<button
  type="button"
  class="hidden"
  aria-hidden="true"
  tabindex="-1"
  data-kt-modal-toggle="#bond_detail_delete_bmk_modal"
></button>

<button
  type="button"
  class="hidden"
  aria-hidden="true"
  tabindex="-1"
  data-kt-modal-toggle="#bond_detail_delete_investor_modal"
></button>

<button
  type="button"
  class="hidden"
  aria-hidden="true"
  tabindex="-1"
  data-kt-modal-toggle="#bond_detail_discard_changes_modal"
></button>

<button
  type="button"
  class="hidden"
  aria-hidden="true"
  tabindex="-1"
  data-kt-modal-toggle="#bond_detail_delete_note_modal"
></button>
